import React, { useEffect, useRef, useState } from "react";
import {
  AppBar,
  Badge,
  Box,
  Container,
  ListItem,
  List,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PersonIcon from "@mui/icons-material/Person";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import HomeRounded from "@mui/icons-material/HomeRounded";
import StorefrontRounded from "@mui/icons-material/StorefrontRounded";
import InfoRounded from "@mui/icons-material/InfoRounded";
import ArticleRounded from "@mui/icons-material/ArticleRounded";

import { GradientBorderBox } from "../../../Components";
import SearchBox from "./SearchBox";
import HamburgerMenu from "./HamburgerMenu";

const navItems = [
  { name: "صفحه نخست", route: "/", icon: <HomeRounded /> },
  { name: "فروشگاه", route: "/products/all", icon: <StorefrontRounded /> },
  { name: "درباره ما", route: "/contact-us", icon: <InfoRounded /> },
  { name: "بلاگ", route: "/blogs", icon: <ArticleRounded /> },
];

export default function Navbar() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const refItems = useRef({});
  const refBg = useRef(null);

  const { list } = useSelector((state) => state.cartSlice);
  const { token } = useSelector((state) => state.userSlice);

  const [indicatorStyle, setIndicatorStyle] = useState({
    right: 0,
    width: 0,
  });

  // Calculate active indicator position for RTL layout
  useEffect(() => {
    const activeItem = navItems?.find((i) => i?.route === location?.pathname);
    if (!activeItem) return;

    const ref = refItems?.current?.[activeItem?.route];
    if (!ref) return;

    const parentWidth = ref?.parentElement?.offsetWidth;
    const offsetRight = parentWidth - (ref?.offsetLeft + ref?.offsetWidth);

    setIndicatorStyle({
      right: offsetRight,
      width: ref?.offsetWidth + 30,
    });
  }, [location?.pathname, refItems]);

  // Render navigation items
  const renderedNavItems = navItems?.map((item, index) => (
    <ListItem
      key={index}
      ref={(tag) => (refItems.current[item?.route] = tag)}
      sx={{
        color:
          location?.pathname === item?.route
            ? theme.palette.primary.main
            : theme.palette.text.primary,
        // fontWeight: location?.pathname === item?.route ? "bold" : "normal",
        transition: "all 0.3s ease",
        width: "max-content",
        cursor: "pointer",
        zIndex: 2,
        "&:hover": {
          color: theme.palette.primary.dark,
        },
      }}
      onClick={() => navigate(item?.route)}
    >
      {item?.name}
    </ListItem>
  ));

  return (
    <>
      <Container
        maxWidth="xxl"
        sx={{
          position: "fixed",
          top: "1.5rem",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
        }}
      >
        <Stack sx={{ position: "relative" }}>
          <AppBar
            elevation={0}
            sx={{
              bgcolor: alpha(theme.palette.background.paper, 0.2),
              backdropFilter: "blur(12px)",
              borderRadius: "3rem",
              position: "static",
              zIndex: 10,
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              px: "1rem",
              height: { xs: "35px", md: "50px", xl: "60px", xxl: "80px" },
              boxShadow: `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.05)}`,
            }}
          >
            {/* Desktop Navigation */}
            <List
              sx={{
                display: { xs: "none", lg: "flex" },
                gap: { xs: ".5rem", lg: ".8rem", xxl: "2rem" },
                position: "relative",
                alignItems: "center",
              }}
            >
              <ListItem
                sx={{
                  width: "max-content",
                  cursor: "pointer",
                  pl: 0,
                }}
                onClick={() => navigate("/")}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: { xs: "10px", lg: "16px", xl: "26px", xxl: "40px" },
                  }}
                >
                  <img
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                    src="/Media/Layout/MainLayout/Navbar/nimobileIcon.png"
                    alt="Logo"
                  />
                </Box>
              </ListItem>
              {renderedNavItems}
            </List>

            {/* Mobile Hamburger Menu */}
            <Stack
              sx={{
                display: { xs: "flex", lg: "none" },
                width: "50%",
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HamburgerMenu navItems={navItems} />
            </Stack>

            {/* User Actions & Search */}
            <Stack
              direction="row-reverse"
              sx={{ gap: "1rem", height: "100%", alignItems: "center" }}
            >
              <PersonIcon
                onClick={() => navigate("/profile")}
                sx={{
                  color: theme.palette.primary.main,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    color: theme.palette.primary.dark,
                    transform: "scale(1.1)",
                  },
                }}
              />

              <Badge
                badgeContent={list?.length || 0}
                color="primary"
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.7rem",
                    minWidth: "1.2rem",
                    height: "1.2rem",
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.primary.main}`,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                <LocalMallIcon
                  onClick={() => navigate("/cart")}
                  sx={{
                    color: theme.palette.primary.main,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      color: theme.palette.primary.dark,
                      transform: "scale(1.1)",
                    },
                  }}
                />
              </Badge>

              <SearchBox />
            </Stack>
          </AppBar>

          <GradientBorderBox borderSize="3px" overHidden={true} radius="3rem" />

          {/* Active Item Glassmorphism Indicator */}
          <Box
            ref={(tag) => (refBg.current = tag)}
            sx={{
              width: indicatorStyle?.width ?? 140,
              height: "40px",
              position: "absolute",
              top: "50%",
              right: indicatorStyle?.right ?? 0,
              transform: "translateY(-50%)",
              zIndex: 1,
              display: { xs: "none", lg: "block" },
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              backdropFilter: "blur(8px)",
              borderRadius: "2rem",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          />
        </Stack>
      </Container>

      <Box mt={{ xs: "70px", sm: "90px", lg: "120px", xxl: "150px" }} />
    </>
  );
}
